import { useRef, useEffect } from "react";
import {
  Box,
  Typography,
  ListItemButton,
  ListItemIcon,
  IconButton,
  Tooltip,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import DescriptionIcon from "@mui/icons-material/Description";
import ChatIcon from "@mui/icons-material/Chat";
import type { GlobalSearchResult } from "../types/globalSearch.types";

interface ResultItemProps {
  result: GlobalSearchResult;
  isSelected: boolean;
  onClick: () => void;
  onChatClick?: () => void;
  index: number;
}

// Helper to safely convert a value to string (handles objects with {data} key)
const toDisplayString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (
    typeof value === "object" &&
    "data" in (value as Record<string, unknown>)
  ) {
    return toDisplayString((value as Record<string, unknown>).data);
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

export default function GlobalSearchResultItem({
  result,
  isSelected,
  onClick,
  onChatClick,
  index,
}: ResultItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);

  // Auto-scroll into view when selected via keyboard navigation
  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [isSelected]);

  const icon =
    result.type === "dataset" ? (
      <StorageIcon color="primary" />
    ) : (
      <DescriptionIcon color="action" />
    );

  const fileName = toDisplayString(result.title) || "Untitled";
  const contentSnippet = toDisplayString(result.description);

  // Get extracted metadata for document info line
  const extractedTitle = result.metadata.extractedTitle
    ? toDisplayString(result.metadata.extractedTitle)
    : null;
  const author = result.metadata.author
    ? toDisplayString(result.metadata.author)
    : null;
  const datasetName = result.metadata.datasetName
    ? toDisplayString(result.metadata.datasetName)
    : null;

  // AI-enriched metadata
  const summary = result.metadata.summary
    ? toDisplayString(result.metadata.summary)
    : null;

  // Use extractedTitle as primary display, fall back to fileName
  const displayTitle = extractedTitle || fileName;

  // Build info line: "filename • by Author • in Dataset"
  const infoParts: string[] = [];
  // Show filename if we're displaying extractedTitle and they differ
  if (extractedTitle && extractedTitle !== fileName) {
    infoParts.push(fileName);
  }
  if (author) {
    infoParts.push(`by ${author}`);
  }
  if (datasetName) {
    infoParts.push(`in ${datasetName}`);
  }
  const infoLine = infoParts.join(" • ");

  const handleChatClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChatClick?.();
  };

  // Determine what to show as description - prefer summary over chunk snippet
  const displayDescription = summary || contentSnippet;

  return (
    <ListItemButton
      ref={itemRef}
      selected={isSelected}
      onClick={onClick}
      data-testid={`global-search-result-${index}`}
      data-attr={`global-search:card:search-result-${index}`}
      sx={{
        py: 1.5,
        px: 2,
        "&.Mui-selected": {
          backgroundColor: "action.selected",
        },
        "&.Mui-selected:hover": {
          backgroundColor: "action.selected",
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Document title (extracted) or file name */}
        <Typography
          variant="body1"
          noWrap
          sx={{ fontWeight: isSelected ? 600 : 400 }}
        >
          {displayTitle}
        </Typography>

        {/* Document info line */}
        {result.type === "chunk" && infoLine && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: "block", mt: 0.25 }}
          >
            {infoLine}
          </Typography>
        )}

        {/* Dataset description */}
        {result.type === "dataset" && result.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: "block", mt: 0.25 }}
          >
            {result.description}
          </Typography>
        )}

        {/* Content snippet or summary */}
        {result.type === "chunk" && displayDescription && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              mt: 0.5,
              fontSize: "0.8rem",
              fontStyle: summary ? "normal" : "normal",
            }}
          >
            {displayDescription}
          </Typography>
        )}
      </Box>

      {/* Actions - only chat icon, removed Document label */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
        {onChatClick && (
          <Tooltip title="Chat about this">
            <IconButton
              size="small"
              onClick={handleChatClick}
              data-testid={`global-search-chat-${index}`}
              data-attr={`global-search:btn:chat-with-result-${index}`}
              sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
            >
              <ChatIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {/* Keep Dataset label only for datasets */}
        {result.type === "dataset" && (
          <Box
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: "action.hover",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Dataset
            </Typography>
          </Box>
        )}
      </Box>
    </ListItemButton>
  );
}
